import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import { useAuth } from './useAuth';

// Time block names to filter from calendar
const TIME_BLOCK_NAMES = [
  'Office Time',
  'Plan & Organize',
  'Projects',
  'Delegate and Inspect',
  'Morning Start',
  'EOD wrap-up'
];

// Firebase callable functions
const getGoogleCalendarAuthUrlFn = httpsCallable(functions, 'getGoogleCalendarAuthUrl');
const getGoogleCalendarEventsFn = httpsCallable(functions, 'getGoogleCalendarEvents');
const createGoogleCalendarEventFn = httpsCallable(functions, 'createGoogleCalendarEvent');
const updateGoogleCalendarEventFn = httpsCallable(functions, 'updateGoogleCalendarEvent');
const deleteGoogleCalendarEventFn = httpsCallable(functions, 'deleteGoogleCalendarEvent');
const disconnectGoogleCalendarFn = httpsCallable(functions, 'disconnectGoogleCalendar');

export function useGoogleCalendar() {
  const { userProfile, user } = useAuth();
  const [events, setEvents] = useState([]);
  const [timeBlocks, setTimeBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Connection status comes from user profile (set by backend)
  const isSignedIn = !!userProfile?.googleCalendarConnected;
  const connectedEmail = userProfile?.googleCalendarEmail;
  const connectedAt = userProfile?.googleCalendarConnectedAt;

  // Check URL params for OAuth callback result
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gcalStatus = params.get('gcal');

    if (gcalStatus === 'connected') {
      // Clear the URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (gcalStatus === 'error') {
      const errorMsg = params.get('error') || 'Failed to connect Google Calendar';
      setError(errorMsg);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Sign in - redirects to Google OAuth
  const signIn = useCallback(async () => {
    if (!user) {
      setError('You must be logged in to connect Google Calendar');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getGoogleCalendarAuthUrlFn();
      const { authUrl } = result.data;

      // Redirect to Google OAuth
      window.location.href = authUrl;
    } catch (err) {
      console.error('Error getting auth URL:', err);
      setError(err.message || 'Failed to start Google Calendar connection');
      setLoading(false);
    }
  }, [user]);

  // Sign out - disconnect Google Calendar
  const signOut = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      await disconnectGoogleCalendarFn();
      setEvents([]);
      setTimeBlocks([]);
    } catch (err) {
      console.error('Error disconnecting:', err);
      setError(err.message || 'Failed to disconnect Google Calendar');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch calendar events via backend
  const fetchEvents = useCallback(async (timeMin, timeMax) => {
    if (!isSignedIn) return [];

    setLoading(true);
    setError(null);

    try {
      const result = await getGoogleCalendarEventsFn({
        timeMin: timeMin || new Date().toISOString(),
        timeMax: timeMax || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      });

      const fetchedEvents = result.data.events || [];
      setEvents(fetchedEvents);
      return fetchedEvents;
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message || 'Failed to fetch calendar events');
      return [];
    } finally {
      setLoading(false);
    }
  }, [isSignedIn]);

  // Create a calendar event via backend
  const createEvent = useCallback(async (eventData) => {
    if (!isSignedIn) {
      setError('Not connected to Google Calendar');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await createGoogleCalendarEventFn({ eventData });
      return result.data.event;
    } catch (err) {
      console.error('Error creating event:', err);
      setError(err.message || 'Failed to create event');
      return null;
    } finally {
      setLoading(false);
    }
  }, [isSignedIn]);

  // Update a calendar event via backend
  const updateEvent = useCallback(async (eventId, eventData) => {
    if (!isSignedIn) {
      setError('Not connected to Google Calendar');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await updateGoogleCalendarEventFn({ eventId, eventData });
      return result.data.event;
    } catch (err) {
      console.error('Error updating event:', err);
      setError(err.message || 'Failed to update event');
      return null;
    } finally {
      setLoading(false);
    }
  }, [isSignedIn]);

  // Delete a calendar event via backend
  const deleteEvent = useCallback(async (eventId) => {
    if (!isSignedIn) {
      setError('Not connected to Google Calendar');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      await deleteGoogleCalendarEventFn({ eventId });
      return true;
    } catch (err) {
      console.error('Error deleting event:', err);
      setError(err.message || 'Failed to delete event');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSignedIn]);

  // Get events for today
  const getTodayEvents = useCallback(async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return fetchEvents(today.toISOString(), tomorrow.toISOString());
  }, [fetchEvents]);

  // Get events for this week
  const getWeekEvents = useCallback(async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekEnd = new Date(today);
    weekEnd.setDate(weekEnd.getDate() + 7);

    return fetchEvents(today.toISOString(), weekEnd.toISOString());
  }, [fetchEvents]);

  // Filter events to only time blocks (matching TIME_BLOCK_NAMES)
  const filterTimeBlocks = useCallback((events) => {
    return events.filter(event => {
      const summary = event.summary?.toLowerCase() || '';
      return TIME_BLOCK_NAMES.some(name =>
        summary.includes(name.toLowerCase())
      );
    });
  }, []);

  // Get time blocks for a specific date
  const getTimeBlocksForDate = useCallback(async (date) => {
    if (!isSignedIn) return [];

    // Parse the date string properly to avoid timezone issues
    let targetDate;
    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = date.split('-').map(Number);
      targetDate = new Date(year, month - 1, day, 0, 0, 0, 0);
    } else {
      targetDate = new Date(date);
      targetDate.setHours(0, 0, 0, 0);
    }

    const nextDay = new Date(targetDate);
    nextDay.setDate(nextDay.getDate() + 1);

    const events = await fetchEvents(targetDate.toISOString(), nextDay.toISOString());
    const blocks = filterTimeBlocks(events);
    setTimeBlocks(blocks);
    return blocks;
  }, [isSignedIn, fetchEvents, filterTimeBlocks]);

  // Get time blocks for today
  const getTodayTimeBlocks = useCallback(async () => {
    return getTimeBlocksForDate(new Date());
  }, [getTimeBlocksForDate]);

  // Get time blocks for this week
  const getWeekTimeBlocks = useCallback(async () => {
    if (!isSignedIn) return [];

    const events = await getWeekEvents();
    const blocks = filterTimeBlocks(events);
    setTimeBlocks(blocks);
    return blocks;
  }, [isSignedIn, getWeekEvents, filterTimeBlocks]);

  // Get time blocks for a specific week (given a start date)
  const getTimeBlocksForWeek = useCallback(async (weekStartDate) => {
    if (!isSignedIn) return [];

    const startDate = new Date(weekStartDate);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 7);

    const events = await fetchEvents(startDate.toISOString(), endDate.toISOString());
    const blocks = filterTimeBlocks(events);
    return blocks;
  }, [isSignedIn, fetchEvents, filterTimeBlocks]);

  // Get ALL events for a specific week (not filtered)
  const getAllEventsForWeek = useCallback(async (weekStartDate) => {
    if (!isSignedIn) return [];

    const startDate = new Date(weekStartDate);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 7);

    const events = await fetchEvents(startDate.toISOString(), endDate.toISOString());
    return events;
  }, [isSignedIn, fetchEvents]);

  // Get ALL events for a specific date (not filtered)
  const getAllEventsForDate = useCallback(async (date) => {
    if (!isSignedIn) return [];

    // Parse the date string properly to avoid timezone issues
    let targetDate;
    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = date.split('-').map(Number);
      targetDate = new Date(year, month - 1, day, 0, 0, 0, 0);
    } else {
      targetDate = new Date(date);
      targetDate.setHours(0, 0, 0, 0);
    }

    const nextDay = new Date(targetDate);
    nextDay.setDate(nextDay.getDate() + 1);

    const events = await fetchEvents(targetDate.toISOString(), nextDay.toISOString());
    return events;
  }, [isSignedIn, fetchEvents]);

  // Helper to build recurrence rule
  const buildRecurrenceRule = useCallback((recurrence, date) => {
    if (!recurrence) return null;

    const d = new Date(date);
    const dayOfWeek = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'][d.getDay()];

    switch (recurrence) {
      case 'DAILY':
        return ['RRULE:FREQ=DAILY'];
      case 'WEEKLY':
        return [`RRULE:FREQ=WEEKLY;BYDAY=${dayOfWeek}`];
      case 'WEEKDAYS':
        return ['RRULE:FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR'];
      case 'BIWEEKLY':
        return [`RRULE:FREQ=WEEKLY;INTERVAL=2;BYDAY=${dayOfWeek}`];
      case 'MONTHLY':
        return ['RRULE:FREQ=MONTHLY'];
      default:
        return null;
    }
  }, []);

  // Create a new time block on the calendar
  const createTimeBlock = useCallback(async (name, date, startTime, endTime, description = '', recurrence = '') => {
    if (!isSignedIn) {
      setError('Not connected to Google Calendar');
      return null;
    }

    // Validate name is a valid time block type
    const isValidName = TIME_BLOCK_NAMES.some(n =>
      n.toLowerCase() === name.toLowerCase()
    );

    if (!isValidName) {
      setError(`Invalid time block name. Must be one of: ${TIME_BLOCK_NAMES.join(', ')}`);
      return null;
    }

    // Build datetime strings
    const dateStr = date instanceof Date
      ? date.toISOString().split('T')[0]
      : date;

    const eventData = {
      summary: name,
      description: description || undefined,
      start: {
        dateTime: `${dateStr}T${startTime}:00`,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      end: {
        dateTime: `${dateStr}T${endTime}:00`,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    };

    // Add recurrence if specified
    const recurrenceRule = buildRecurrenceRule(recurrence, date);
    if (recurrenceRule) {
      eventData.recurrence = recurrenceRule;
    }

    const event = await createEvent(eventData);

    // Refresh time blocks after creation
    if (event) {
      await getTimeBlocksForDate(date);
    }

    return event;
  }, [isSignedIn, createEvent, getTimeBlocksForDate, buildRecurrenceRule]);

  return {
    isSignedIn,
    isConfigured: true, // Always configured with backend approach
    connectedEmail,
    connectedAt,
    events,
    timeBlocks,
    loading,
    error,
    signIn,
    signOut,
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    getTodayEvents,
    getWeekEvents,
    getTimeBlocksForDate,
    getTodayTimeBlocks,
    getWeekTimeBlocks,
    getTimeBlocksForWeek,
    getAllEventsForWeek,
    getAllEventsForDate,
    createTimeBlock,
    filterTimeBlocks,
    TIME_BLOCK_NAMES
  };
}

// Export time block names for use elsewhere
export { TIME_BLOCK_NAMES };

// Helper to format event time
export function formatEventTime(event) {
  if (event.start?.dateTime) {
    const start = new Date(event.start.dateTime);
    const end = new Date(event.end?.dateTime);
    return `${start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }
  // All day event
  return 'All day';
}

// Helper to check if event is happening now
export function isEventNow(event) {
  if (!event.start?.dateTime) return false;
  const now = new Date();
  const start = new Date(event.start.dateTime);
  const end = new Date(event.end?.dateTime);
  return now >= start && now <= end;
}

// Helper to get event color
export function getEventColor(event) {
  const colorMap = {
    '1': '#7986CB', // Lavender
    '2': '#33B679', // Sage
    '3': '#8E24AA', // Grape
    '4': '#E67C73', // Flamingo
    '5': '#F6BF26', // Banana
    '6': '#F4511E', // Tangerine
    '7': '#039BE5', // Peacock
    '8': '#616161', // Graphite
    '9': '#3F51B5', // Blueberry
    '10': '#0B8043', // Basil
    '11': '#D50000', // Tomato
  };
  return colorMap[event.colorId] || '#C45D3A';
}
